import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { collection, CollectionReference, doc, getDocs, query, setDoc, where } from "firebase/firestore";
import { useFirebase } from '../components/Firebase';
import { DateTime } from 'luxon';
import { handleQuerySnapshot } from '../utilities';
import { IFirebaseRaceDetails } from '../interfaces/firebaseEntities';

const defaultRaceData = {
  hostName: '',
  sku: '',
  dateStr: '',
  startTime: ''
}

interface RaceHostSelectionContextType {
  selectedHosts: Record<string, IFirebaseRaceDetails>;
  setHostForSession: (sessionId: string, data: IFirebaseRaceDetails) => void;
  getHostForSession: (sessionId: string) => IFirebaseRaceDetails;
}

const RaceHostSelectionContext = createContext<RaceHostSelectionContextType>({
  selectedHosts: {},
  setHostForSession: () => {},
  getHostForSession: () => defaultRaceData,
});

export const RaceHostSelectionProvider: React.FC<{ children: React.ReactNode, date: Date }> = ({ children, date }) => {
  const [selectedHosts, setSelectedHosts] = useState<Record<string, IFirebaseRaceDetails>>({});
  const firebase = useFirebase();
  const db = useMemo(() => firebase.db, [firebase]);
  const raceDetailsRef = useMemo(() =>collection(db, "race_details") as CollectionReference<IFirebaseRaceDetails>,[db]);

  const setHostForSession = useCallback(async (sessionId: string, data: IFirebaseRaceDetails) => {
    setSelectedHosts(oldState => ({ ...oldState, [sessionId]: data }));
    await setDoc(doc(raceDetailsRef, sessionId), data);
  }, [raceDetailsRef]);

  const getHostForSession = useCallback((sessionId: string) => {
    return selectedHosts[sessionId] || '';
  }, [selectedHosts]);

  const refreshRaceDetails = useCallback(async () => {
    const q = query(raceDetailsRef, where("dateStr", "==", DateTime.fromJSDate(date).toFormat("dd-MM-yyyy")));
    const snapshot = await getDocs(q);
    const raceDetails = handleQuerySnapshot<IFirebaseRaceDetails>(snapshot);
    setSelectedHosts(raceDetails);
  }, [raceDetailsRef, date]);

  useEffect(() => {
    refreshRaceDetails();
  }, [refreshRaceDetails]);
  

  return (
    <RaceHostSelectionContext.Provider value={{ selectedHosts, setHostForSession, getHostForSession }}>
      {children}
    </RaceHostSelectionContext.Provider>
  );
};

export const useRaceHostSelection = () => useContext(RaceHostSelectionContext);